import type {
  ExternalToolBatch,
  RecentlyFlushedResultCheck,
  BatchDeclarationGate,
  ToolCallExpectedGates,
} from './external-tool-batch.types';
import {
  abortBatchDeclarationGate,
  abortToolCallExpectedGates,
} from './external-tool-batch-waiters';

export function getRequiredToolCallIds(batch: ExternalToolBatch): string[] {
  if (batch.expectedToolCallIds.size === 0) {
    return Array.from(batch.requests.keys());
  }

  return Array.from(
    new Set([...batch.expectedToolCallIds, ...batch.requests.keys()]),
  );
}

export function pruneStaleExpectedToolCallIds(batch: ExternalToolBatch): void {
  if (batch.results.size === 0) {
    return;
  }

  for (const toolCallId of batch.expectedToolCallIds) {
    if (
      batch.pendingExecuteToolCallIds.has(toolCallId) ||
      batch.requests.has(toolCallId) ||
      batch.results.has(toolCallId) ||
      batch.lostExecuteToolCallIds.has(toolCallId)
    ) {
      continue;
    }

    batch.expectedToolCallIds.delete(toolCallId);
  }
}

export function getExpectedRequiredToolCallIds(
  batch: ExternalToolBatch,
): string[] {
  return getRequiredToolCallIds(batch).filter((toolCallId) =>
    batch.expectedToolCallIds.has(toolCallId),
  );
}

export function getUndeclaredRegisteredToolCallIds(
  batch: ExternalToolBatch,
): string[] {
  return Array.from(batch.requests.keys()).filter(
    (toolCallId) =>
      !batch.expectedToolCallIds.has(toolCallId) &&
      !batch.pendingExecuteToolCallIds.has(toolCallId),
  );
}

export function hasBlockingPendingExecute(batch: ExternalToolBatch): boolean {
  for (const toolCallId of batch.pendingExecuteToolCallIds) {
    if (!batch.results.has(toolCallId)) {
      return true;
    }
  }

  return false;
}

export function isBatchReady({
  batch,
  batchId,
  inFlightHandlerCount,
  hasRecentlyFlushedResult,
  wasDelivered = () => false,
}: {
  batch: ExternalToolBatch;
  batchId: string;
  inFlightHandlerCount: number;
  hasRecentlyFlushedResult: RecentlyFlushedResultCheck;
  wasDelivered?: RecentlyFlushedResultCheck;
}): boolean {
  if (inFlightHandlerCount > 0) {
    return false;
  }

  if (hasBlockingPendingExecute(batch)) {
    return false;
  }

  pruneStaleExpectedToolCallIds(batch);

  const undeclaredRegisteredToolCallIds =
    getUndeclaredRegisteredToolCallIds(batch);

  if (
    !undeclaredRegisteredToolCallIds.every((toolCallId) =>
      batch.results.has(toolCallId),
    )
  ) {
    return false;
  }

  const expectedRequiredToolCallIds = getExpectedRequiredToolCallIds(batch);

  if (
    expectedRequiredToolCallIds.length > 0 &&
    !expectedRequiredToolCallIds.every((toolCallId) =>
      batch.results.has(toolCallId),
    )
  ) {
    return false;
  }

  return (
    getReadyToolCallIdsForFlush({
      batch,
      batchId,
      inFlightHandlerCount,
      hasRecentlyFlushedResult,
      wasDelivered,
    }).length > 0
  );
}

export function getReadyToolCallIdsForFlush({
  batch,
  batchId,
  inFlightHandlerCount,
  hasRecentlyFlushedResult,
  wasDelivered = () => false,
}: {
  batch: ExternalToolBatch;
  batchId: string;
  inFlightHandlerCount: number;
  hasRecentlyFlushedResult: RecentlyFlushedResultCheck;
  wasDelivered?: RecentlyFlushedResultCheck;
}): string[] {
  if (inFlightHandlerCount > 0) {
    return [];
  }

  if (hasBlockingPendingExecute(batch)) {
    return [];
  }

  pruneStaleExpectedToolCallIds(batch);

  const notYetDelivered = (toolCallId: string) =>
    !wasDelivered({
      batchId,
      chatId: batch.chatId,
      toolCallId,
    });
  const expectedReadyToolCallIds = getExpectedRequiredToolCallIds(batch).filter(
    (toolCallId) =>
      batch.results.has(toolCallId) && notYetDelivered(toolCallId),
  );
  const undeclaredReadyToolCallIds = getUndeclaredRegisteredToolCallIds(
    batch,
  ).filter(
    (toolCallId) =>
      batch.results.has(toolCallId) && notYetDelivered(toolCallId),
  );
  const retryReadyToolCallIds = getRequiredToolCallIds(batch).filter(
    (toolCallId) =>
      batch.results.has(toolCallId) &&
      hasRecentlyFlushedResult({
        batchId,
        chatId: batch.chatId,
        toolCallId,
      }) &&
      !batch.expectedToolCallIds.has(toolCallId),
  );

  return [
    ...expectedReadyToolCallIds,
    ...undeclaredReadyToolCallIds,
    ...retryReadyToolCallIds,
  ];
}

export function getBatchAliasIds({
  batches,
  batchId,
}: {
  batches: Map<string, ExternalToolBatch>;
  batchId: string;
}): string[] {
  const batch = batches.get(batchId);

  if (!batch) {
    return [batchId];
  }

  const aliasIds: string[] = [];

  for (const [aliasId, candidate] of batches) {
    if (candidate === batch) {
      aliasIds.push(aliasId);
    }
  }

  return aliasIds.length > 0 ? aliasIds : [batchId];
}

export function getLiveBatchAliasId({
  batches,
  batch,
}: {
  batches: Map<string, ExternalToolBatch>;
  batch: ExternalToolBatch;
}): string | undefined {
  if (batches.get(batch.flushBatchId) === batch) {
    return batch.flushBatchId;
  }

  for (const [aliasId, candidate] of batches) {
    if (candidate === batch) {
      return aliasId;
    }
  }

  return undefined;
}

export function isToolCallIdKnownToBatch({
  batch,
  toolCallId,
}: {
  batch: ExternalToolBatch;
  toolCallId: string;
}): boolean {
  return (
    batch.requests.has(toolCallId) ||
    batch.pendingExecuteToolCallIds.has(toolCallId) ||
    batch.expectedToolCallIds.has(toolCallId) ||
    batch.lostExecuteToolCallIds.has(toolCallId) ||
    batch.results.has(toolCallId)
  );
}

export function getWorkToken(
  workTokenByChatId: Map<string, number>,
  chatId: string,
): number {
  return workTokenByChatId.get(chatId) ?? 0;
}

export function isBatchWorkCurrent({
  batch,
  workTokenByChatId,
}: {
  batch: ExternalToolBatch;
  workTokenByChatId: Map<string, number>;
}): boolean {
  return batch.workToken === getWorkToken(workTokenByChatId, batch.chatId);
}

export function incrementWorkToken({
  workTokenByChatId,
  chatId,
}: {
  workTokenByChatId: Map<string, number>;
  chatId: string;
}): number {
  const nextToken = getWorkToken(workTokenByChatId, chatId) + 1;

  workTokenByChatId.set(chatId, nextToken);

  return nextToken;
}

export function getBatchForCompleteRequest({
  batches,
  handlerWorkTokenByBatchId,
  workTokenByChatId,
  batchId,
  chatId,
}: {
  batches: Map<string, ExternalToolBatch>;
  handlerWorkTokenByBatchId: Map<string, number>;
  workTokenByChatId: Map<string, number>;
  batchId: string;
  chatId: string;
}): ExternalToolBatch | null {
  const batch = batches.get(batchId);
  const currentToken = getWorkToken(workTokenByChatId, chatId);
  const handlerToken = handlerWorkTokenByBatchId.get(batchId);

  if (handlerToken !== undefined && handlerToken !== currentToken) {
    return null;
  }

  if (!batch || batch.workToken !== currentToken) {
    return null;
  }

  return batch;
}

export function isStaleCompleteRequest({
  batches,
  handlerWorkTokenByBatchId,
  workTokenByChatId,
  batchId,
  chatId,
}: {
  batches: Map<string, ExternalToolBatch>;
  handlerWorkTokenByBatchId: Map<string, number>;
  workTokenByChatId: Map<string, number>;
  batchId: string;
  chatId: string;
}): boolean {
  const batch = batches.get(batchId);
  const currentToken = getWorkToken(workTokenByChatId, chatId);
  const handlerToken = handlerWorkTokenByBatchId.get(batchId);

  if (handlerToken !== undefined && handlerToken !== currentToken) {
    return true;
  }

  return !!batch && batch.workToken !== currentToken;
}

export function resolveExternalToolBatchAliasIds({
  batchId,
  chatId,
  resumeGroupId,
}: {
  batchId: string;
  chatId: string;
  resumeGroupId?: string;
}): string[] {
  return [
    ...new Set([batchId, ...(resumeGroupId ? [resumeGroupId, chatId] : [])]),
  ];
}

export function resolveSharedBatchFlushKey({
  batches,
  batchId,
}: {
  batches: Map<string, ExternalToolBatch>;
  batchId: string;
}): string {
  const batch = batches.get(batchId);

  if (!batch) {
    return batchId;
  }

  for (const [aliasId, candidate] of batches) {
    if (candidate === batch) {
      return aliasId;
    }
  }

  return batchId;
}

export function getOrCreateExternalToolBatch({
  batches,
  workTokenByChatId,
  batchId,
  chatId,
}: {
  batches: Map<string, ExternalToolBatch>;
  workTokenByChatId: Map<string, number>;
  batchId: string;
  chatId: string;
}): ExternalToolBatch {
  const existingBatch = batches.get(batchId);

  if (existingBatch) {
    if (existingBatch.chatId !== chatId) {
      existingBatch.chatId = chatId;
    }

    return existingBatch;
  }

  const nextBatch: ExternalToolBatch = {
    chatId,
    flushBatchId: batchId,
    workToken: getWorkToken(workTokenByChatId, chatId),
    declared: false,
    adoptedOntoChatId: false,
    expectedToolCallIds: new Set(),
    lostExecuteToolCallIds: new Set(),
    pendingExecuteToolCallIds: new Set(),
    requests: new Map(),
    results: new Map(),
    toolNameByToolCallId: new Map(),
  };

  batches.set(batchId, nextBatch);

  return nextBatch;
}

export function pickExternalToolFlushBatchId({
  incomingBatchId,
}: {
  incomingBatchId: string;
}): string {
  return incomingBatchId;
}

export function aliasExternalToolBatch({
  batches,
  batch,
  aliasIds,
  incomingBatchId,
}: {
  batches: Map<string, ExternalToolBatch>;
  batch: ExternalToolBatch;
  aliasIds: string[];
  incomingBatchId: string;
}): void {
  batch.flushBatchId = pickExternalToolFlushBatchId({ incomingBatchId });

  for (const aliasId of aliasIds) {
    const current = batches.get(aliasId);

    if (current && current !== batch) {
      continue;
    }

    batches.set(aliasId, batch);
  }
}

export function deleteAliasedExternalToolBatches({
  batches,
  batch,
  batchDeclarationGates,
  toolCallExpectedGates,
}: {
  batches: Map<string, ExternalToolBatch>;
  batch: ExternalToolBatch;
  batchDeclarationGates: Map<string, BatchDeclarationGate>;
  toolCallExpectedGates: ToolCallExpectedGates;
}): void {
  for (const [aliasId, candidate] of [...batches.entries()]) {
    if (candidate !== batch) {
      continue;
    }

    batches.delete(aliasId);

    const declarationGate = batchDeclarationGates.get(aliasId);

    if (declarationGate && declarationGate.status !== 'pending') {
      if (aliasId === batch.chatId) {
        batchDeclarationGates.delete(aliasId);
      } else {
        abortBatchDeclarationGate({
          gates: batchDeclarationGates,
          batchId: aliasId,
          tombstone: true,
        });
      }
    }

    const expectedGates = toolCallExpectedGates.get(aliasId);

    if (!expectedGates) {
      continue;
    }

    if (aliasId === batch.chatId) {
      for (const [toolCallId, gate] of [...expectedGates.entries()]) {
        if (gate.status !== 'pending') {
          expectedGates.delete(toolCallId);
        }
      }

      if (expectedGates.size === 0) {
        toolCallExpectedGates.delete(aliasId);
      }

      continue;
    }

    abortToolCallExpectedGates({
      gates: toolCallExpectedGates,
      batchId: aliasId,
      tombstone: true,
    });
  }
}
